/**
 * Tenant service
 * Service for managing tenants via the API
 */
import type {
  Tenant,
  TenantRequest,
  TenantErrorResponse,
  SignatureConfig,
  CreateTenantRequest,
  UpdateTenantRequest,
  UpdateSignatureConfigRequest,
  CreateTenantResponse,
  UpdateTenantResponse,
  DeleteTenantResponse,
  AddUseCaseRequest,
  PlatformVersion,
  CreatePlatformVersionRequest,
  TenantCascadePreview,
} from '../Components/BUPlatformList/BUPlatformList.types';
import type { UseCase } from '../../WhatsNew/Types/WhatsNew.types';
import { authenticatedFetch } from '@/Auth/services/auth.service';
import { env } from '@/config/env';

const TENANTS_ENDPOINT = '/v1/tenants';

/**
 * Get tenants with optional filters
 */
export const getTenants = async (params: TenantRequest = {}): Promise<Tenant[]> => {
  try {
    // Build query string from params
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.includeInactive) queryParams.append('includeInactive', 'true');

    const queryString = queryParams.toString();
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to fetch tenants',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('403: You do not have permission to view tenants');
      }

      throw new Error(errorData.message || 'Failed to fetch tenants');
    }

    const json = await response.json();
    // BFF returns paginated response { data, pagination }
    const data: Tenant[] = Array.isArray(json) ? json : json.data;
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Get a specific tenant by slug
 */
export const getTenantBySlug = async (slug: string): Promise<Tenant> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to fetch tenant',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('403: You do not have permission to view this tenant');
      }

      if (response.status === 404) {
        throw new Error('404: Tenant not found');
      }

      throw new Error(errorData.message || 'Failed to fetch tenant');
    }

    const data: Tenant = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Get the signature configuration for a specific tenant
 */
export const getTenantSignatureConfig = async (slug: string): Promise<SignatureConfig> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/signature-config`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to fetch signature config',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('403: You do not have permission to view this signature config');
      }

      if (response.status === 404) {
        throw new Error('404: Signature config not found for this tenant');
      }

      throw new Error(errorData.message || 'Failed to fetch signature config');
    }

    const data: SignatureConfig = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Update the signature configuration for a specific tenant
 */
export const updateTenantSignatureConfig = async (
  slug: string,
  payload: UpdateSignatureConfigRequest,
): Promise<SignatureConfig> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/signature-config`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to update signature config',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('403: You do not have permission to update this signature config');
      }

      if (response.status === 404) {
        throw new Error('404: Signature config not found for this tenant');
      }

      throw new Error(errorData.message || 'Failed to update signature config');
    }

    const data: SignatureConfig = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Create a new tenant
 */
export const createTenant = async (payload: CreateTenantRequest): Promise<CreateTenantResponse> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to create tenant',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('403: You do not have permission to create tenants');
      }

      if (response.status === 409) {
        throw new Error('409: Tenant already exists');
      }

      throw new Error(errorData.message || 'Failed to create tenant');
    }

    const data: Tenant = await response.json();
    return {
      success: true,
      message: 'Tenant created successfully',
      data,
    };
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Update a tenant
 */
export const updateTenant = async (
  slug: string,
  payload: UpdateTenantRequest,
): Promise<UpdateTenantResponse> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to update tenant',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('403: You do not have permission to update tenants');
      }

      if (response.status === 404) {
        throw new Error('404: Tenant not found');
      }

      throw new Error(errorData.message || 'Failed to update tenant');
    }

    const data: Tenant = await response.json();
    return {
      success: true,
      message: 'Tenant updated successfully',
      data,
    };
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Restore a disabled tenant
 */
export const restoreTenant = async (slug: string): Promise<DeleteTenantResponse> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/restore`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to restore tenant',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('403: You do not have permission to restore tenants');
      }

      if (response.status === 404) {
        throw new Error('404: Tenant not found');
      }

      if (response.status === 409) {
        throw new Error('409: Tenant is already active');
      }

      throw new Error(errorData.message || 'Failed to restore tenant');
    }

    return {
      success: true,
      message: 'Tenant restored successfully',
    };
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * List all use cases for a tenant
 */
export const getTenantUseCases = async (slug: string): Promise<UseCase[]> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/usecases`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to fetch use cases',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('403: You do not have permission to view use cases');
      }

      if (response.status === 404) {
        throw new Error('404: Tenant not found');
      }

      throw new Error(errorData.message || 'Failed to fetch use cases');
    }

    const data: UseCase[] = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Delete a tenant
 */
export const deleteTenant = async (slug: string): Promise<DeleteTenantResponse> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}`;

    const response = await authenticatedFetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to delete tenant',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('403: You do not have permission to delete tenants');
      }

      if (response.status === 404) {
        throw new Error('404: Tenant not found');
      }

      throw new Error(errorData.message || 'Failed to delete tenant');
    }

    return {
      success: true,
      message: 'Tenant deleted successfully',
    };
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Add a use case to a tenant
 */
export const addTenantUseCase = async (
  slug: string,
  payload: AddUseCaseRequest,
): Promise<UseCase> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/usecases`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to add use case',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('403: You do not have permission to manage use cases');
      }

      if (response.status === 404) {
        throw new Error('404: Tenant not found');
      }

      if (response.status === 409) {
        throw new Error('409: Use case already exists for this tenant');
      }

      throw new Error(errorData.message || 'Failed to add use case');
    }

    const data: UseCase = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * List platform versions for a tenant
 */
export const getTenantVersions = async (
  slug: string,
  params: { includeInactive?: boolean } = {},
): Promise<PlatformVersion[]> => {
  try {
    const query = params.includeInactive ? '?includeInactive=true' : '';
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/versions${query}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to fetch versions',
        statusCode: response.status,
      }));
      throw new Error(errorData.message || 'Failed to fetch versions');
    }

    const data: PlatformVersion[] = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Push a new platform version for a tenant
 */
export const createTenantVersion = async (
  slug: string,
  payload: CreatePlatformVersionRequest,
): Promise<PlatformVersion> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/versions`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to create version',
        statusCode: response.status,
      }));
      if (response.status === 409) throw new Error('409: Version already exists for this tenant');
      throw new Error(errorData.message || 'Failed to create version');
    }

    const data: PlatformVersion = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Set a platform version as the current version for a tenant
 */
export const setCurrentTenantVersion = async (
  slug: string,
  versionId: string,
): Promise<PlatformVersion> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/versions/${versionId}/set-current`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to set current version',
        statusCode: response.status,
      }));
      throw new Error(errorData.message || 'Failed to set current version');
    }

    const data: PlatformVersion = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Disable (soft-delete) a platform version for a tenant
 */
export const removeTenantVersion = async (
  slug: string,
  versionId: string,
): Promise<{ message: string }> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/versions/${versionId}`;

    const response = await authenticatedFetch(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to remove version',
        statusCode: response.status,
      }));
      if (response.status === 400) throw new Error(errorData.message || 'Cannot remove version');
      throw new Error(errorData.message || 'Failed to remove version');
    }

    return response.json();
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Toggle a use case's active status for a tenant
 */
export const toggleTenantUseCase = async (slug: string, useCaseId: string): Promise<UseCase> => {
  try {
    const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/usecases/${useCaseId}/toggle`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: TenantErrorResponse = await response.json().catch(() => ({
        message: 'Failed to toggle use case',
        statusCode: response.status,
      }));

      if (response.status === 403) {
        throw new Error('403: You do not have permission to manage use cases');
      }

      if (response.status === 404) {
        throw new Error('404: Use case or tenant not found');
      }

      throw new Error(errorData.message || 'Failed to toggle use case');
    }

    const data: UseCase = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Count what the tenant carries with it, for the disable and restore confirmations.
 */
export const getTenantCascadePreview = async (slug: string): Promise<TenantCascadePreview> => {
  const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/cascade-preview`;

  const response = await authenticatedFetch(url);

  if (!response.ok) {
    const errorData: TenantErrorResponse = await response.json().catch(() => ({
      message: 'Failed to load the cascade preview',
      statusCode: response.status,
    }));

    throw new Error(errorData.message || 'Failed to load the cascade preview');
  }

  return (await response.json()) as TenantCascadePreview;
};

/**
 * Bring a disabled platform version back.
 *
 * Only flips `isActive`: the version returns to the registry without being promoted. Promoting is
 * still `setCurrentTenantVersion`, the same as after a push.
 */
export const restoreTenantVersion = async (
  slug: string,
  versionId: string,
): Promise<PlatformVersion> => {
  const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/versions/${versionId}/restore`;

  const response = await authenticatedFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorData: TenantErrorResponse = await response.json().catch(() => ({
      message: 'Failed to restore the version',
      statusCode: response.status,
    }));

    throw new Error(errorData.message || 'Failed to restore the version');
  }

  return (await response.json()) as PlatformVersion;
};

/**
 * Permanently delete a platform version.
 *
 * The BFF refuses unless nothing points at it: not the tenant's current version, and no What's New
 * at all — soft-deleted ones included, since the foreign key survives the soft delete. Its 409
 * message says which of the two blocked it, so it is worth surfacing verbatim.
 */
export const purgeTenantVersion = async (
  slug: string,
  versionId: string,
): Promise<{ message: string }> => {
  const url = `${env.bffBaseURL}${TENANTS_ENDPOINT}/${slug}/versions/${versionId}/permanent`;

  const response = await authenticatedFetch(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorData: TenantErrorResponse = await response.json().catch(() => ({
      message: 'Failed to delete the version',
      statusCode: response.status,
    }));

    throw new Error(errorData.message || 'Failed to delete the version');
  }

  return (await response.json()) as { message: string };
};
